import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import { FiTrendingUp, FiAward, FiX } from 'react-icons/fi';
import AlertDialog from '../Components/Shared/AlertDialog';
import { useResourcePermissions } from '../../../utils/useResourcePermissions';
import './../Styles/LmsDashboard.css';
import './../../../style/Vars.css';
import './../../../style/ContainersStyles.css';
import './../../../style/ElementsStyle.css';


// Custom Modal Component since reactstrap is not available
const Modal = ({ isOpen, toggle, children }) => {
    if (!isOpen) return null;
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
        }} onClick={toggle}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                width: '90%',
                maxWidth: '800px', // Wider modal
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'slideUp 0.3s ease-out',
                maxHeight: '90vh', // Ensure it finds on screen
                display: 'flex',
                flexDirection: 'column'
            }} onClick={e => e.stopPropagation()}>
                {children}
            </div>
            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title
);

const DashboardGeneral = () => {
    const { puedeExportar } = useResourcePermissions('LMS_DASHBOARD');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState([]);
    const [flipped, setFlipped] = useState(false);
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "Información", message: "", variant: "info" });

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [loadingStudent, setLoadingStudent] = useState(false);

    const handleStudentClick = async (student) => {
        setLoadingStudent(true);
        setShowModal(true);
        // Pre-fill with known data (name, grade)
        setSelectedStudent(student);
        try {
            const res = await axios.get(LMS_ENDPOINTS.estadisticasEstudianteDetalle(student.id));
            // Merge detailed info with existing info (like grade)
            setSelectedStudent(prev => {
                const detail = res.data || {};
                return {
                    ...detail, // Detail data (e.g. FotoPerfil)
                    ...prev,   // Summary data (Name, Company, Unit, Area, Puesto) - PRIORITIZED
                    // Ensure specific fields handle casing and fallback correctly
                    fotoPerfil: detail.fotoPerfil || detail.FotoPerfil || prev.fotoPerfil || prev.FotoPerfil,
                    promedioGeneral: prev.promedioGeneral ?? detail.promedioGeneral ?? detail.PromedioGeneral
                };
            });
        } catch (error) {
            console.error('Error loading student details:', error);
        } finally {
            setLoadingStudent(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedStudent(null);
    };

    const loadData = async (activeFilters) => {
        setLoading(true);
        try {
            const params = {};
            if (activeFilters?.empresa)      params.empresa    = activeFilters.empresa;
            if (activeFilters?.unidadOrg)    params.unidad     = activeFilters.unidadOrg;
            if (activeFilters?.areaNegocio)  params.area       = activeFilters.areaNegocio;
            if (activeFilters?.tipoHabilidad) params.habilidad = activeFilters.tipoHabilidad;
            if (activeFilters?.estado)       params.estado     = activeFilters.estado;
            if (activeFilters?.curso)        params.idCurso    = activeFilters.curso;

            const idCurso = params.idCurso || 0;
            delete params.idCurso;

            const res = await axios.get(LMS_ENDPOINTS.estadisticasCursoByIdCurso(idCurso), { params });
            setStats(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Carga inicial sin filtros para poblar las opciones de los dropdowns
    const [allStats, setAllStats] = useState([]);
    const loadAllForOptions = async () => {
        try {
            const res = await axios.get(LMS_ENDPOINTS.estadisticasCursoByIdCurso(0));
            setAllStats(Array.isArray(res.data) ? res.data : []);
        } catch { /* silencioso */ }
    };

    const [exporting, setExporting] = useState(false);

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const response = await axios.get(LMS_ENDPOINTS.estadisticasExportarExcel, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Dashboard_General_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting excel:', error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al exportar el archivo Excel. Por favor, intente de nuevo más tarde.",
                variant: "error",
            });
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        loadAllForOptions();
    }, []);

    // Filters State
    const [filters, setFilters] = useState({
        empresa: '',
        unidadOrg: '',
        estado: '',
        areaNegocio: '',
        tipoHabilidad: '',
        curso: ''
    });

    // Re-cargar datos del backend cuando cambian los filtros
    useEffect(() => {
        loadData(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Extract unique options from unfiltered data
    const filterOptions = useMemo(() => {
        const options = {
            empresas: new Set(),
            unidadesOrg: new Set(),
            areasNegocio: new Set(),
            tiposHabilidad: new Set(),
            cursos: new Map()
        };

        allStats.forEach(s => {
            const empresa = s.nombreEmpresa || s.NombreEmpresa;
            const unidad = s.nombreUnidadOrganizacional || s.NombreUnidadOrganizacional;
            const area = s.areaNegocio || s.AreaNegocio;
            const habilidad = s.tipoHabilidad || s.TipoHabilidad;
            const idCurso = s.idCurso ?? s.IdCurso;
            const nombreCurso = s.nombreCurso || s.NombreCurso;

            if (empresa) options.empresas.add(empresa);
            if (unidad) options.unidadesOrg.add(unidad);
            if (area) options.areasNegocio.add(area);
            if (habilidad) options.tiposHabilidad.add(habilidad);
            if (idCurso && nombreCurso) {
                options.cursos.set(idCurso, nombreCurso);
            }
        });

        return {
            empresas: Array.from(options.empresas).sort(),
            unidadesOrg: Array.from(options.unidadesOrg).sort(),
            areasNegocio: Array.from(options.areasNegocio).sort(),
            tiposHabilidad: Array.from(options.tiposHabilidad).sort(),
            cursos: Array.from(options.cursos.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
        };
    }, [allStats]);

    // Los datos ya vienen filtrados del backend — se usan directamente
    const filteredStats = stats;

    // Data processing for Pie Chart
    const pieData = useMemo(() => {
        const counts = { aprovados: 0, reprobados: 0, enCurso: 0 };
        filteredStats.forEach(s => {
            const isCertified = s.tieneCertificado || s.TieneCertificado;
            const isFinished = s.terminado || s.Terminado;

            if (isCertified) {
                counts.aprovados++;
            } else if (isFinished) {
                counts.reprobados++;
            } else {
                counts.enCurso++;
            }
        });

        return {
            labels: ['Aprobados (Certificados)', 'Reprobados', 'En Curso'],
            datasets: [
                {
                    data: [counts.aprovados, counts.reprobados, counts.enCurso],
                    backgroundColor: [
                        '#10b981', // green
                        '#ef4444', // red
                        '#f59e0b', // amber
                    ],
                    borderWidth: 0,
                },
            ],
        };
    }, [filteredStats]);

    // Data processing for Stacked Bar Chart
    const barData = useMemo(() => {
        const coursesMap = {};
        filteredStats.forEach(s => {
            const courseName = s.nombreCurso || s.NombreCurso || 'Sin nombre';
            const isCertified = s.tieneCertificado || s.TieneCertificado;
            const isFinished = s.terminado || s.Terminado;

            if (!coursesMap[courseName]) {
                coursesMap[courseName] = { aprovados: 0, reprobados: 0, enCurso: 0 };
            }

            if (isCertified) {
                coursesMap[courseName].aprovados++;
            } else if (isFinished) {
                coursesMap[courseName].reprobados++;
            } else {
                coursesMap[courseName].enCurso++;
            }
        });

        const labels = Object.keys(coursesMap);
        return {
            labels,
            datasets: [
                {
                    label: 'Aprobados',
                    data: labels.map(l => coursesMap[l].aprovados),
                    backgroundColor: '#10b981',
                },
                {
                    label: 'Reprobados',
                    data: labels.map(l => coursesMap[l].reprobados),
                    backgroundColor: '#ef4444',
                },
                {
                    label: 'En Curso',
                    data: labels.map(l => coursesMap[l].enCurso),
                    backgroundColor: '#f59e0b',
                },
            ],
        };
    }, [filteredStats]);

    const topUsers = useMemo(() => {
        const studentMap = {};

        filteredStats.forEach(s => {
            const isFinished = s.terminado || s.Terminado;
            const avg = s.promedioGeneral ?? s.PromedioGeneral;
            const studentId = s.idEstudiante ?? s.IdEstudiante;
            const studentName = s.nombreCompleto || s.NombreCompleto;

            if (isFinished && avg !== null) {
                if (!studentMap[studentId]) {
                    studentMap[studentId] = {
                        id: studentId,
                        nombreCompleto: studentName,
                        areaNegocio: s.areaNegocio || s.AreaNegocio,
                        puesto: s.puesto || s.Puesto,
                        nombreEmpresa: s.nombreEmpresa || s.NombreEmpresa,
                        nombreUnidadOrganizacional: s.nombreUnidadOrganizacional || s.NombreUnidadOrganizacional,
                        totalScore: 0,
                        courseCount: 0
                    };
                }
                studentMap[studentId].totalScore += Number(avg);
                studentMap[studentId].courseCount += 1;
            }
        });

        return Object.values(studentMap)
            .map(s => ({
                id: s.id,
                nombreCompleto: s.nombreCompleto,
                areaNegocio: s.areaNegocio,
                puesto: s.puesto,
                nombreEmpresa: s.nombreEmpresa,
                nombreUnidadOrganizacional: s.nombreUnidadOrganizacional,
                promedioGeneral: s.totalScore / s.courseCount,
                courseCount: s.courseCount
            }))
            .sort((a, b) => b.promedioGeneral - a.promedioGeneral)
            .slice(0, 10);
    }, [filteredStats]);

    const lineData = useMemo(() => {
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

        // Obtenemos los últimos 6 meses
        const last6Months = [];
        for (let i = 5; i >= 0; i--) {
            const date = new Date();
            date.setMonth(date.getMonth() - i);
            last6Months.push({
                month: date.getMonth(),
                year: date.getFullYear(),
                label: monthNames[date.getMonth()]
            });
        }

        const started = last6Months.map(() => 0);
        const finished = last6Months.map(() => 0);

        filteredStats.forEach(s => {
            const inicio = s.ultimoAcceso || s.UltimoAcceso;
            const fin = s.fechaTermino || s.FechaTermino;
            const esTerminado = s.terminado || s.Terminado || s.tieneCertificado || s.TieneCertificado;

            if (inicio) {
                const dInicio = new Date(inicio);
                const idx = last6Months.findIndex(m => m.month === dInicio.getMonth() && m.year === dInicio.getFullYear());
                if (idx !== -1) started[idx]++;
            }

            if (esTerminado && fin) {
                const dFin = new Date(fin);
                const idx = last6Months.findIndex(m => m.month === dFin.getMonth() && m.year === dFin.getFullYear());
                if (idx !== -1) finished[idx]++;
            }
        });

        // Helper inside useMemo or useEffect to get styles
        const getCssVar = (name, fallback) => {
            if (typeof window === 'undefined') return fallback;
            const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
            return val || fallback;
        };

        const primaryColor = getCssVar('--color-primary', '#BE0032');
        const darkColor = getCssVar('--color-text', '#111827');

        return {
            labels: last6Months.map(m => m.label),
            datasets: [
                {
                    label: 'Cursos Iniciados',
                    data: started,
                    borderColor: primaryColor,
                    tension: 0.4,
                },
                {
                    label: 'Cursos Terminados',
                    data: finished,
                    borderColor: darkColor,
                    tension: 0.4,
                },
            ],
        };
    }, [filteredStats]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                display: true,
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11,
                        family: "'Outfit', 'Inter', sans-serif"
                    },
                    boxWidth: 8
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1e293b',
                bodyColor: '#64748b',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                cornerRadius: 12,
                padding: 10,
                displayColors: true,
                usePointStyle: true
            }
        },
        layout: {
            padding: {
                top: 10,
                bottom: 10,
                left: 10,
                right: 10
            }
        }
    };

    const barOptions = {
        ...chartOptions,
        scales: {
            x: { stacked: true },
            y: { stacked: true },
        }
    };

    return (
        <>
            <div className="lms-dashboard-container" style={{ position: 'relative' }}>
                {loading && (
                    <div className="catalog-loading" style={{
                        position: 'fixed',
                        top: '20px',
                        right: '50%',
                        transform: 'translateX(50%)',
                        zIndex: 9999,
                        background: 'rgba(255,255,255,0.9)',
                        padding: '10px 20px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        fontSize: '0.85rem',
                        fontWeight: '700',
                        color: 'var(--color-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        pointerEvents: 'none'
                    }}>
                        <div className="spinner-border spinner-border-sm" role="status" style={{ width: '1rem', height: '1rem' }}></div>
                        Actualizando datos...
                    </div>
                )}

                <div className={`lms-dashboard-content-wrapper ${loading ? 'is-loading' : ''}`} style={{
                    transition: 'opacity 0.4s ease',
                    opacity: loading ? 0.7 : 1,
                    pointerEvents: loading ? 'none' : 'auto'
                }}>

                <div className="lms-dashboard-filters">
                    <div className="lms-filter-label">Filtrar por:</div>


                    <select
                        className="lms-filter-select"
                        value={filters.unidadOrg}
                        onChange={e => setFilters({ ...filters, unidadOrg: e.target.value })}
                    >
                        <option value="">Todas las Unidades</option>
                        {filterOptions.unidadesOrg.map((e, i) => <option key={i} value={e}>{e}</option>)}
                    </select>

                    <select
                        className="lms-filter-select"
                        value={filters.estado}
                        onChange={e => setFilters({ ...filters, estado: e.target.value })}
                    >
                        <option value="">Todos los Estados</option>
                        <option value="Aprobado">Aprobado</option>
                        <option value="En Curso">En Curso</option>
                        <option value="Reprobado">Reprobado</option>
                    </select>

                    <select
                        className="lms-filter-select"
                        value={filters.areaNegocio}
                        onChange={e => setFilters({ ...filters, areaNegocio: e.target.value })}
                    >
                        <option value="">Todas las Áreas</option>
                        {filterOptions.areasNegocio.map((e, i) => <option key={i} value={e}>{e}</option>)}
                    </select>

                    <select
                        className="lms-filter-select"
                        value={filters.tipoHabilidad}
                        onChange={e => setFilters({ ...filters, tipoHabilidad: e.target.value })}
                    >
                        <option value="">Todas las Habilidades</option>
                        {filterOptions.tiposHabilidad.map((e, i) => <option key={i} value={e}>{e}</option>)}
                    </select>

                    <select
                        className="lms-filter-select"
                        value={filters.curso}
                        onChange={e => setFilters({ ...filters, curso: e.target.value })}
                    >
                        <option value="">Todos los Cursos</option>
                        {filterOptions.cursos.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>

                    {puedeExportar && (
                        <button
                            className="roles-btn"
                            onClick={handleExportExcel}
                            disabled={exporting}
                            title="Descargar Reporte"
                            style={{ height: '42px', padding: '0 1.25rem' }}
                        >
                            {exporting ? (
                                "Cargando..."
                            ) : (
                                <>
                                    📥 Reporte
                                </>
                            )}
                        </button>
                    )}
                </div>

                <div className="lms-dashboard-grid">
                    {/* Left Column - Large Flip Card (2/3) */}
                    <div className="lms-dashboard-left">
                        <div className={`card-flip-container ${flipped ? 'flipped' : ''}`}>
                            <div className="card-flip-inner">
                                {/* Front: Pie Chart */}
                                <div className="card-front dashboard-stat-card">
                                    <div className="card-header">
                                        <h3 className="card-title">Distribución General de Progreso</h3>
                                        <button className="flip-btn" onClick={() => setFlipped(true)} title="Ver gráfica de barras">
                                            <FiTrendingUp />
                                        </button>
                                    </div>
                                    <div style={{ flex: 1, position: 'relative', minHeight: '300px' }}>
                                        <Pie data={pieData} options={chartOptions} />
                                    </div>
                                </div>

                                {/* Back: Stacked Bar Chart */}
                                <div className="card-back dashboard-stat-card">
                                    <div className="card-header">
                                        <h3 className="card-title">Progreso Detallado por Curso</h3>
                                        <button className="flip-btn" onClick={() => setFlipped(false)} title="Ver gráfica de pastel">
                                            <FiTrendingUp />
                                        </button>
                                    </div>
                                    <div style={{ flex: 1, position: 'relative', minHeight: '300px' }}>
                                        <Bar data={barData} options={barOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column (1/3) */}
                    <div className="lms-dashboard-right">
                        {/* Top Card: Line Chart */}
                        <div className="dashboard-stat-card half-height">
                            <div style={{ padding: '1.25rem 1.5rem', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <div className="card-header">
                                    <h3 className="card-title">Cursos Iniciados vs Terminados</h3>
                                </div>
                                <div style={{ flex: 1, position: 'relative', minHeight: '200px' }}>
                                    <Line data={lineData} options={chartOptions} />
                                </div>
                            </div>
                        </div>

                        {/* Bottom Card: Top Users List */}
                        <div className="dashboard-stat-card half-height">
                            <div style={{ padding: '1.25rem 1.5rem', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <div className="card-header">
                                    <h3 className="card-title">Mejores Promedios</h3>
                                    <FiAward style={{ color: '#f59e0b' }} />
                                </div>
                                <div className="top-users-list">
                                    {topUsers.length === 0 ? (
                                        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>No hay datos suficientes.</p>
                                    ) : (
                                        topUsers.map((u, i) => (
                                            <div key={i} className="user-item" onClick={() => handleStudentClick(u)} style={{ cursor: 'pointer' }}>
                                                <div className="user-avatar">{u.nombreCompleto.charAt(0)}</div>
                                                <div className="user-info">
                                                    <div className="user-name">{u.nombreCompleto}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                        ({u.courseCount} {u.courseCount === 1 ? 'curso' : 'cursos'})
                                                    </div>
                                                </div>
                                                <div className="user-avg">
                                                    {Math.round(u.promedioGeneral)}%
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

            {/* Modal Detalle Estudiante */}
            <Modal isOpen={showModal} toggle={closeModal}>
                <div className="modal-header-custom">
                    <h5 className="modal-title-custom">Perfil del Estudiante</h5>
                    <button type="button" onClick={closeModal} className="modal-close-btn">
                        <FiX />
                    </button>
                </div>

                <div className="modal-body-custom">
                    {loadingStudent ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p style={{ marginTop: '1rem', color: '#64748b' }}>Cargando perfil...</p>
                        </div>
                    ) : selectedStudent ? (() => {
                        const studentCourses = stats ? stats.filter(s => {
                            const sId = s.idEstudiante || s.IdEstudiante;
                            const targetId = selectedStudent.id || selectedStudent.IdEstudiante || selectedStudent.idEstudiante;
                            return String(sId) === String(targetId);
                        }) : [];

                        const certificatesCount = studentCourses.filter(c => c.tieneCertificado || c.TieneCertificado).length;

                        return (
                            <div className="student-modal-grid">
                                {/* Left Column: Avatar + Profile Info + Details */}
                                <div className="student-info-sidebar">
                                    <div className="student-profile-group">
                                        <div className="student-avatar-small">
                                            {(selectedStudent.fotoPerfil || selectedStudent.FotoPerfil) ? (
                                                <img src={selectedStudent.fotoPerfil || selectedStudent.FotoPerfil} alt={selectedStudent.nombreCompleto} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div className="avatar-fallback-small">
                                                    {selectedStudent.nombreCompleto?.charAt(0)}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="student-name-large">
                                                {selectedStudent.nombreCompleto}
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="student-details">
                                        <div>
                                            <span className="detail-label">Empresa</span>
                                            <div className="detail-value">
                                                {selectedStudent.nombreEmpresa || selectedStudent.NombreEmpresa || 'No registrada'}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="detail-label">Unidad Organizacional</span>
                                            <div className="detail-value">
                                                {selectedStudent.nombreUnidadOrganizacional || selectedStudent.NombreUnidadOrganizacional || 'No asignada'}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="detail-label">Área de Negocio</span>
                                            <div className="detail-value">
                                                {selectedStudent.areaNegocio || selectedStudent.AreaNegocio || 'No asignada'}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="detail-label">Puesto</span>
                                            <div className="detail-value">
                                                {selectedStudent.puesto || selectedStudent.Puesto || 'No asignado'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column: Stats Row + Courses */}
                                <div className="student-content-main">
                                    <div className="student-header">
                                        <div className="stats-row">
                                            {/* Courses Count */}
                                            <div className="student-grade-badge">
                                                <div className="grade-value" style={{ background: '#f1f5f9', color: '#0f172a' }}>
                                                    {studentCourses.length}
                                                </div>
                                                <div className="grade-label">CURSOS</div>
                                            </div>

                                            {/* Certificates Count */}
                                            <div className="student-grade-badge">
                                                <div className="grade-value" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                                    {certificatesCount}
                                                </div>
                                                <div className="grade-label">CERTIFICADOS</div>
                                            </div>

                                            {/* Average Grade */}
                                            {selectedStudent.promedioGeneral !== undefined && (
                                                <div className="student-grade-badge">
                                                    <div
                                                        className="grade-value"
                                                        style={{
                                                            background: selectedStudent.promedioGeneral >= 60 ? '#dcfce7' : '#fee2e2',
                                                            color: selectedStudent.promedioGeneral >= 60 ? '#166534' : '#991b1b',
                                                        }}
                                                    >
                                                        {Math.round(selectedStudent.promedioGeneral)}%
                                                    </div>
                                                    <div className="grade-label">PROMEDIO</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <h4 className="courses-section-title">
                                        <FiAward size={18} /> Cursos Registrados
                                    </h4>

                                    <div className="course-list-scroll" style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '200px' }}>
                                        {studentCourses.length > 0 ? (
                                            studentCourses.map((curso, idx) => (
                                                <div key={idx} className="course-item-card">
                                                    <div className="course-info-main">
                                                        <div className="course-name">
                                                            {curso.nombreCurso || curso.NombreCurso}
                                                        </div>
                                                        <div className="course-status">
                                                            {(curso.tieneCertificado || curso.TieneCertificado) ? (
                                                                <span style={{ color: '#10b981' }}>• Certificado</span>
                                                            ) : (curso.terminado || curso.Terminado) ? (
                                                                <span style={{ color: '#ef4444' }}>• Finalizado (Sin Certificado)</span>
                                                            ) : (
                                                                <span style={{ color: '#f59e0b' }}>• En Curso</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="course-grade">
                                                        {Math.round(curso.promedioGeneral || curso.PromedioGeneral || 0)}%
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: '12px' }}>
                                                No hay cursos registrados para este periodo.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })() : (
                        <div className="text-center text-muted py-5">No se encontró información.</div>
                    )}
                </div>
            </Modal >


            {/* Modal de alertas estándar LMS */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                buttonText="Aceptar"
            />
        </>
    );
};

export default DashboardGeneral;
